import { UseInfiniteQueryOptions, UseQueryOptions } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import { Children, cloneElement } from "react";
import { IPagination } from "../../../../api/GenericTypes";

interface ChildProps {
  onEmptyCallback: () => void;
  infiniteQueryOptions?: UseInfiniteQueryOptions<any, unknown, IPagination<any>, any>;
  entityCountOptions?: UseQueryOptions<any, unknown, { count: number }>;
}

interface EntitySidebarTileAutosorterProps {
  children: React.ReactNode;
}

/**
 * A React component that manages and sorts its child components dynamically.
 * It separates children into two groups: `remainingChildren` and `processedChildren`.
 * When a child triggers the `onEmptyCallback`, it is moved from `remainingChildren` to `processedChildren`.
 * @author @CorradoSurmanowicz
 * @param {EntitySidebarTileAutosorterProps} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to be managed and sorted.
 *
 * @returns {JSX.Element} A JSX element containing the dynamically managed children.
 *
 * @remarks
 * - Each child component is expected to accept an `onEmptyCallback` prop, which is invoked when the child is "processed."
 * - The `onEmptyCallback` is used to move the child from the `remainingChildren` array to the `processedChildren` array.
 * - The `processedChildren` are rendered after the `remainingChildren` in the output.
 *
 * @example
 * ```tsx
 * <EntitySidebarTileAutosorter>
 *   <ChildComponent onEmptyCallback={() => {}} />
 *   <ChildComponent onEmptyCallback={() => {}} />
 * </EntitySidebarTileAutosorter>
 * ```
 */
export const EntitySidebarTileAutosorter = ({ children }: EntitySidebarTileAutosorterProps) => {
  // Use a Map for efficient child lookup by key
  const childMap = React.useMemo(() => {
    const map = new Map<React.Key, React.ReactElement<any>>();
    Children.forEach(children, (child, idx) => {
      const element = child as React.ReactElement<any>;
      const key = element?.key ?? `child-${idx}`;
      map.set(key, element);
    });
    return map;
  }, [children]);

  const [processedKeys, setProcessedKeys] = useState<Set<React.Key>>(new Set());

  // Memoize the callback for each key
  const onEmptyCallback = useCallback((key: React.Key) => {
    setProcessedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Reset processedKeys when children change
  React.useEffect(() => {
    setProcessedKeys(new Set());
  }, [children]);

  // Compute remaining and processed keys only once per render
  const [remainingKeys, processedKeysArr] = React.useMemo(() => {
    const allKeys = Array.from(childMap.keys());
    const processedArr: React.Key[] = [];
    const remainingArr: React.Key[] = [];
    for (const key of allKeys) {
      if (processedKeys.has(key)) {
        processedArr.push(key);
      } else {
        remainingArr.push(key);
      }
    }
    return [remainingArr, processedArr];
  }, [childMap, processedKeys]);

  return (
    <>
      {remainingKeys.map((key) => {
        const element = childMap.get(key)!;
        if (element == null) return <></>;
        return cloneElement(element as React.ReactElement<ChildProps>, {
          key,
          onEmptyCallback: () => onEmptyCallback(key),
          infiniteQueryOptions: {
            cacheTime: 30 * 1000, // 30 sec,
            staleTime: 20 * 1000, // 20 sec
          },
          entityCountOptions: {
            cacheTime: 30 * 1000, // 30 sec,
            staleTime: 20 * 1000, // 20 sec
          },
        });
      })}
      {processedKeysArr.map((key) => {
        const element = childMap.get(key);
        if (element == null) return <></>;
        return <React.Fragment key={`processed-${key}`}>{element}</React.Fragment>;
      })}
    </>
  );
};
